import React, { useState } from 'react'
import { AccountCommentsDto } from '../../../interactors/gen/backendClient'
import { Paper, Typography, IconButton, Tooltip, Box, TextField, Button } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import dayjs from 'dayjs'
import { useAccountsStore } from '../../../store/AccountsStore'
import { fullName } from '../../../domain/Account/AccountDisplay'
import { isAdmin } from '../../../domain/Account/isAdmin'

interface Props {
  comment: AccountCommentsDto
  onDelete: (commentId: string) => void
  onEdit: (commentId: string, comment: string) => void
}

export const MemberCommentItem: React.FC<Props> = ({ comment, onDelete, onEdit }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedComment, setEditedComment] = useState(comment.comment)
  const accountsStore = useAccountsStore()

  const createdBy = accountsStore.members.find((member) => member.id === comment.createdBy)
  const isOwnComment = accountsStore.connectedAccount?.id === comment.createdBy
  const isUserAdmin = accountsStore.connectedAccount ? isAdmin(accountsStore.connectedAccount) : false
  const canEditOrDelete = isOwnComment || isUserAdmin

  const handleEdit = () => {
    onEdit(comment.id, editedComment)
    setIsEditing(false)
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 0,
        position: 'relative',
        backgroundColor: '#fff',
        borderColor: 'rgba(122, 129, 151, 0.18)',
        borderRadius: 2,
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#7A8197', fontWeight: 700 }}>
        {`Écrit par ${createdBy ? fullName(createdBy) : comment.createdBy} le ${dayjs(comment.createdAt).format(
          'DD/MM/YYYY'
        )}`}
      </Typography>
      {isEditing ? (
        <>
          <TextField
            variant="outlined"
            fullWidth
            multiline
            value={editedComment}
            onChange={(e) => setEditedComment(e.target.value)}
            sx={{
              mb: 1.25,
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
              '& .MuiOutlinedInput-input': { fontSize: 14 },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="text"
              color="inherit"
              onClick={() => setIsEditing(false)}
              sx={{ height: 34, borderRadius: '999px', px: 1.5, textTransform: 'none', fontWeight: 800 }}
            >
              Annuler
            </Button>
            <Button
              variant="outlined"
              onClick={handleEdit}
              sx={{
                height: 34,
                borderRadius: '999px',
                px: 1.75,
                borderColor: '#1F3764',
                color: '#1F3764',
                fontWeight: 800,
                textTransform: 'none',
                '&:hover': { borderColor: '#1F3764', backgroundColor: 'rgba(31, 55, 100, 0.06)' },
              }}
            >
              Soumettre
            </Button>
          </Box>
        </>
      ) : (
        <Typography sx={{ fontSize: 14, color: '#1F3764', fontWeight: 600, whiteSpace: 'pre-wrap' }}>
          {comment.comment}
        </Typography>
      )}

      {canEditOrDelete && (
        <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.5 }}>
          {!isEditing && (
            <Tooltip
              title={isOwnComment ? 'Modifier mon commentaire' : 'Modifier ce commentaire (Admin)'}
              placement="top"
            >
              <IconButton
                onClick={() => setIsEditing(true)}
                size="small"
                sx={{
                  borderRadius: 2,
                  '&:hover': { backgroundColor: 'rgba(31, 55, 100, 0.06)', color: '#1F3764' },
                }}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={isOwnComment ? `Supprimer mon commentaire` : `Supprimer ce commentaire (Admin)`}
            placement="top"
          >
            <span>
              <IconButton
                onClick={() => onDelete(comment.id)}
                size="small"
                sx={{
                  borderRadius: 2,
                  '&:hover': { backgroundColor: 'rgba(188, 128, 118, 0.10)', color: '#BC8076' },
                }}
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </Paper>
  )
}
